import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Overview from './pages/Overview';
import Audience from './pages/Audience';
import Acquisition from './pages/Acquisition';
import Behavior from './pages/Behavior';
import Technology from './pages/Technology';
import Events from './pages/Events';
import CustomReport from './pages/CustomReport';
import Revenue from './pages/Revenue';
import Funnel from './pages/Funnel';
import AiInsights from './pages/AiInsights';
import { getProperties } from './api';

const PAGE_TITLES = {
  overview:    'Overview',
  audience:    'Audience',
  acquisition: 'Acquisition',
  behavior:    'Behavior',
  technology:  'Technology',
  events:      'Events',
  custom:      'Custom Report',
  insights:    'AI Insights',
  revenue:     'Revenue & Monetization',
  funnel:      'Funnel Analysis',
};

function fmtDate(d) { return d.toISOString().split('T')[0]; }

export default function App() {
  const [page, setPage] = useState('overview');
  const [properties, setProperties] = useState([]);
  const [selectedProp, setSelectedProp] = useState('');
  const [selectedPropName, setSelectedPropName] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [initLoading, setInitLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  // ── Date range ─────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return fmtDate(d);
  });
  const [endDate, setEndDate] = useState(fmtDate(new Date()));

  // ── Theme ─────────────────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('ga-theme') || 'dark'; } catch { return 'dark'; }
  });

  useEffect(() => {
    // Apply theme to <html> so CSS vars work globally
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('ga-theme', theme); } catch {}
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  // ── Load Properties ────────────────────────────────────────────────────
  useEffect(() => {
    getProperties()
      .then(res => {
        const props = res.data.properties || [];
        setProperties(props);
        if (props.length > 0) {
          setSelectedProp(props[0].id);
          setSelectedPropName(props[0].name);
        }
      })
      .catch(console.error)
      .finally(() => setInitLoading(false));
  }, []);

  const handlePropChange = (id) => {
    setSelectedProp(id);
    const p = properties.find(x => x.id === id);
    setSelectedPropName(p?.name || id);
    setRefreshKey(k => k + 1);
  };

  const handleDateChange = (s, e) => { setStartDate(s); setEndDate(e); };
  const handleRefresh    = ()      => setRefreshKey(k => k + 1);

  const pageProps = {
    propertyId:   selectedProp,
    propertyName: selectedPropName,
    startDate,
    endDate,
    key: refreshKey,
  };

  const renderPage = () => {
    if (initLoading) return (
      <div style={styles.initLoad}>
        <div style={{ fontSize: 48, color: 'var(--accent)', animation: 'spin 1s linear infinite' }}>◈</div>
        <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 16 }}>Connecting to Google Analytics...</div>
      </div>
    );
    switch (page) {
      case 'overview':    return <Overview {...pageProps} />;
      case 'audience':    return <Audience {...pageProps} />;
      case 'acquisition': return <Acquisition {...pageProps} />;
      case 'behavior':    return <Behavior {...pageProps} />;
      case 'technology':  return <Technology {...pageProps} />;
      case 'events':      return <Events {...pageProps} />;
      case 'custom':      return <CustomReport {...pageProps} />;
      case 'insights':    return <AiInsights {...pageProps} />;
      case 'revenue':     return <Revenue {...pageProps} />;
      case 'funnel':      return <Funnel {...pageProps} />;
      default:            return null;
    }
  };

  return (
    <div style={styles.app}>
      <Sidebar
        active={page}
        onChange={setPage}
        properties={properties}
        selectedProp={selectedProp}
        onPropChange={handlePropChange}
        theme={theme}
        onToggleTheme={toggleTheme}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div style={styles.main} className="app-main">
        <Header
          title={PAGE_TITLES[page]}
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
          onRefresh={handleRefresh}
          loading={false}
          theme={theme}
          onToggleTheme={toggleTheme}
          onMenuClick={() => setMobileOpen(true)}
        />
        <div style={styles.content}>{renderPage()}</div>
      </div>
    </div>
  );
}

const styles = {
  app:     { display: 'flex', minHeight: '100vh', background: 'var(--bg)', transition: 'background 0.25s' },
  main:    { flex: 1, marginLeft: 200, minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  content: { flex: 1, padding: '24px 28px', animation: 'fadeIn 0.3s ease' },
  initLoad:{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' },
};
